/**
 * この実装はcross-storageほぼそのままです。
 */
type Method = "get" | "set" | "del" | "getKeys" | "clear";

export interface Permission {
  origin: RegExp;
  allow: Method[];
}

const context: {
  permissions?: Permission[];
} = {};

/**
 * Do not use this directly. This specifications are subject to significant change.
 * @internal
 */
export const init = (permissions: Permission[]) => {
  context.permissions = permissions;
  window.addEventListener("message", listener, false);
  window.parent.postMessage("cross-storage:ready", "*");
};

const permitted = (origin: string, method: Method) => {
  const available = ["get", "set", "del", "clear", "getKeys"];
  if (!available.includes(method)) {
    return false;
  }

  if (context.permissions === undefined) {
    return false;
  }

  for (let i = 0; i < context.permissions.length; i++) {
    const entry = context.permissions[i];
    const match = entry.origin.test(origin);
    if (match && entry.allow.includes(method)) {
      return true;
    }
  }
  return false;
};

const methods: {
  // eslint-disable-next-line @typescript-eslint/ban-types
  [key in Method]: Function;
} = {
  get: (params: { keys: string[] }) => {
    const storage = window.localStorage;
    const result = [];
    for (let i = 0; i < params.keys.length; i++) {
      const value = storage.getItem(params.keys[i]) || null;
      result.push(value);
    }
    // Todo: このパターン好きじゃない
    return result.length > 1 ? result : result[0];
  },
  set: (params: { key: string; value: string }) => {
    window.localStorage.setItem(params.key, params.value);
  },
  del: (params: { keys: string[] }) => {
    for (var i = 0; i < params.keys.length; i++) {
      window.localStorage.removeItem(params.keys[i]);
    }
  },
  getKeys: () => {
    const keys = [];
    const length = window.localStorage.length;

    for (let i = 0; i < length; i++) {
      keys.push(window.localStorage.key(i));
    }

    return keys;
  },
  clear: () => {
    window.localStorage.clear();
  },
};

interface Message {
  origin: string;
  data: string;
}

const listener = (message: Message) => {
  // Messages can be sent in various formats, so limit it to string
  if (typeof message.data !== "string") {
    return;
  }
  // postMessage returns the string "null" as the origin for "file://"
  const origin = message.origin === "null" ? "file://" : message.origin;

  // Handle polling for a ready message
  if (message.data === "cross-storage:poll") {
    return window.parent.postMessage("cross-storage:ready", message.origin);
  }

  // Ignore the ready message when viewing the hub directly
  if (message.data === "cross-storage:ready") return;

  const request: {
    id: string;
    method?: string;
    // eslint-disable-next-line @typescript-eslint/ban-types
    params: Object;
  } = JSON.parse(message.data);

  // Check whether request.method is a string
  if (!request || typeof request.method !== "string") {
    return;
  }

  const method = request.method.split("cross-storage:")[1] as Method;

  let error = "";
  let result: string | undefined = undefined;

  if (!method) {
    return;
  } else if (!permitted(origin, method)) {
    error = "Invalid permissions for " + method;
  } else {
    try {
      result = methods[method](request.params);
    } catch (err) {
      error = (err as Error).message;
    }
  }

  const response = JSON.stringify({
    id: request.id,
    error: error,
    result: result,
  });

  // postMessage requires that the target origin be set to "*" for "file://"
  const targetOrigin = origin === "file://" ? "*" : origin;

  window.parent.postMessage(response, targetOrigin);
};
